import { useCallback } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { FingerPrintIcon } from '@heroicons/react/24/outline';
import { Route, Routes } from 'react-router';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import Switch from 'src/common/primitives/Switch';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import { addATrip } from '../add-trip/addATrip.slice';
import { PassengerExportButton } from './export/PassengerExportButton';
import { PassengerMergeModal } from './management/merge/PassengerMergeModal';
import { PassengerSearchForm } from './management/PassengerSearchForm';
import {
  clearFilters,
  passengerSelectors,
  setPassengerMergeModalOpen,
  setShowDeletedPassengers,
} from './passenger.slice';
import { useIsPassengerMergeEnabledForOrganization } from './useIsPassengerMergeEnabledForOrganization';

export function PassengerRouteActions() {
  return (
    <Routes>
      {/* TODO: element is placeholder here to silence warning. Eventually there will be an add passenger button */}
      <Route index element={<PassengerIndexRouteActions />} />
    </Routes>
  );
}

export function PassengerIndexRouteActions() {
  const { isAdmin, isDispatcher, isOwner, isBambi, isRideOrderingPortalUser } =
    useUserRoles();
  const canExportPassengers = isAdmin || isOwner || isBambi;

  const passengerMergeModalOpen = useAppSelector(
    (state) => state.passenger.passengerMergeModalOpen
  );
  const showDeletedPassengers = useAppSelector(
    (state) => state.passenger.showDeletedPassengers
  );
  const dispatch = useAppDispatch();

  const isPassengerMergeEnabledForOrganization =
    useIsPassengerMergeEnabledForOrganization();

  const canMerge =
    (isPassengerMergeEnabledForOrganization &&
      (isAdmin || isDispatcher || isOwner)) ||
    isBambi;

  const hasFilters = useAppSelector(passengerSelectors.hasFilters);

  const handleCreateNewPassenger = useCallback(() => {
    dispatch(addATrip.actions.selectNoPassenger());
    dispatch(addATrip.actions.selectPassenger({}));
    dispatch(addATrip.actions.setIsEditingPassenger(true));
  }, [dispatch]);

  const handleClearFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  return (
    <div className="flex flex-row items-center gap-4">
      {!isRideOrderingPortalUser && (
        <label className="flex items-center gap-2 text-sm">
          <Switch
            label="Show deleted passengers"
            inputProps={{
              value: showDeletedPassengers,
              onChange: (nextValue: boolean) => {
                dispatch(setShowDeletedPassengers(nextValue));
              },
            }}
          />
          Show deleted passengers
        </label>
      )}
      {hasFilters && (
        <Button variant="anchor" onClick={handleClearFilters}>
          clear filters
        </Button>
      )}
      {canExportPassengers && <PassengerExportButton />}
      {canMerge ? (
        <Button
          variant="primary-outline"
          onClick={() => dispatch(setPassengerMergeModalOpen(true))}
        >
          <FingerPrintIcon className="mr-1 w-5" />
          Merge Passengers
        </Button>
      ) : null}
      <PassengerSearchForm />
      {isRideOrderingPortalUser ? null : (
        <Button variant="primary" onClick={handleCreateNewPassenger}>
          Create a Passenger
        </Button>
      )}
      <PassengerMergeModal
        open={passengerMergeModalOpen}
        setOpen={(open) => dispatch(setPassengerMergeModalOpen(open))}
      />
    </div>
  );
}
